import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { useServerFn } from "@tanstack/react-start";
import { Nav } from "@/components/site/Nav";
import { Footer } from "@/components/site/Closing";
import { submitSupportRequest } from "@/lib/support.functions";
import { SupportSubmission } from "@/lib/support-submission";

export const Route = createFileRoute("/support")({
  head: () => ({
    meta: [
      { title: "Customer Support | ClutchCube" },
      { name: "description", content: "Contact ClutchCube customer support. Submit bug reports, billing questions, feature requests, and other inquiries." },
      { property: "og:title", content: "Customer Support | ClutchCube" },
      { property: "og:description", content: "Reach the ClutchCube support team." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: SupportPage,
});

const EVENT_TYPES: Array<{ value: string; label: string }> = [
  { value: "support_request", label: "Support Request" },
  { value: "bug_report", label: "Bug Report" },
  { value: "billing_issue", label: "Billing Issue" },
  { value: "feature_request", label: "Feature Request" },
  { value: "complaint", label: "Complaint" },
  { value: "cancellation_risk", label: "Cancellation Concern" },
  { value: "onboarding_help", label: "Onboarding Help" },
  { value: "other", label: "Other" },
];

const PRIORITIES = [
  { value: "low", label: "Low" },
  { value: "normal", label: "Normal" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

const CONTACT_METHODS = ["Email", "Phone", "Either"];

const clientSchema = z.object({
  name: z.string().trim().min(2, "Name is required").max(120),
  email: z.string().trim().email("Valid email required").max(200),
  company: z.string().trim().max(200).optional(),
  phone: z.string().trim().max(40).optional(),
  event_type: z.string().min(1, "Select an issue type"),
  priority: z.string().min(1, "Select a priority"),
  subject: z.string().trim().min(2, "Subject is required").max(200),
  description: z.string().trim().min(5, "Please describe your issue").max(5000),
  preferred_contact_method: z.string().trim().max(40).optional(),
});

function SupportPage() {
  const submit = useServerFn(submitSupportRequest);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [submission] = useState(() => new SupportSubmission<z.infer<typeof clientSchema> & { website: string }>());

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setServerError(null);
    const fd = new FormData(e.currentTarget);
    const raw = {
      name: String(fd.get("name") || ""),
      email: String(fd.get("email") || ""),
      company: String(fd.get("company") || ""),
      phone: String(fd.get("phone") || ""),
      event_type: String(fd.get("event_type") || ""),
      priority: String(fd.get("priority") || ""),
      subject: String(fd.get("subject") || ""),
      description: String(fd.get("description") || ""),
      preferred_contact_method: String(fd.get("preferred_contact_method") || ""),
    };
    const parsed = clientSchema.safeParse(raw);
    if (!parsed.success) {
      const err: Record<string, string> = {};
      for (const i of parsed.error.issues) err[String(i.path[0])] = i.message;
      setErrors(err);
      return;
    }
    setErrors({});
    if (!submission.begin()) return;
    setSubmitting(true);
    try {
      const result = await submit({
        data: submission.payloadFor({
          ...parsed.data,
          website: String(fd.get("website") || ""),
        }, {
          page_url: typeof window !== "undefined" ? window.location.href : "",
          user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        }),
      });
      if (!result.ok) {
        setServerError(result.error || "Unable to submit. Please try again.");
      } else {
        setSubmitted(true);
      }
    } catch (err) {
      setServerError((err as Error).message || "Network error. Please try again.");
    } finally {
      submission.end();
      setSubmitting(false);
    }
  };

  const field = "w-full bg-background border border-border rounded-sm px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition";
  const label = "block text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-2";

  return (
    <div className="min-h-screen bg-background">
      <Nav />
      <main className="pt-32 pb-20">
        <section className="mx-auto max-w-4xl px-6">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-primary mb-4">Customer Support</p>
            <h1 className="font-display text-4xl sm:text-6xl leading-tight">
              How can we <span className="text-gradient-ember">help</span>?
            </h1>
            <p className="mt-6 text-muted-foreground max-w-xl mx-auto">
              Submit a support request and our team will get back to you promptly.
            </p>
          </div>

          {submitted ? (
            <div className="relative bg-card border border-primary/40 rounded-sm p-12 text-center shadow-glow">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/20 border border-primary mb-6">
                <span className="text-primary text-2xl">✓</span>
              </div>
              <h2 className="font-display text-3xl">Request Received</h2>
              <p className="mt-4 text-muted-foreground max-w-md mx-auto">
                Thank you. Our support team has been notified and will follow up shortly.
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="relative bg-card border border-border rounded-sm p-8 sm:p-10 shadow-elevated">
              <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px", height: 0, width: 0, overflow: "hidden" }}>
                <label htmlFor="website">Website</label>
                <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" />
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className={label} htmlFor="name">Full Name</label>
                  <input id="name" name="name" className={field} placeholder="Jane Smith" />
                  {errors.name && <p className="mt-2 text-xs text-destructive">{errors.name}</p>}
                </div>
                <div>
                  <label className={label} htmlFor="email">Email</label>
                  <input id="email" name="email" type="email" className={field} placeholder="you@example.com" />
                  {errors.email && <p className="mt-2 text-xs text-destructive">{errors.email}</p>}
                </div>
                <div>
                  <label className={label} htmlFor="company">Company <span className="normal-case text-muted-foreground/70">(optional)</span></label>
                  <input id="company" name="company" className={field} placeholder="Acme Inc." />
                </div>
                <div>
                  <label className={label} htmlFor="phone">Phone <span className="normal-case text-muted-foreground/70">(optional)</span></label>
                  <input id="phone" name="phone" className={field} placeholder="+1 (555) 000-0000" />
                </div>
                <div>
                  <label className={label} htmlFor="event_type">Issue Type</label>
                  <select id="event_type" name="event_type" defaultValue="" className={field}>
                    <option value="" disabled>Select…</option>
                    {EVENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  {errors.event_type && <p className="mt-2 text-xs text-destructive">{errors.event_type}</p>}
                </div>
                <div>
                  <label className={label} htmlFor="priority">Priority</label>
                  <select id="priority" name="priority" defaultValue="normal" className={field}>
                    {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                  {errors.priority && <p className="mt-2 text-xs text-destructive">{errors.priority}</p>}
                </div>
                <div className="sm:col-span-2">
                  <label className={label} htmlFor="subject">Subject</label>
                  <input id="subject" name="subject" className={field} placeholder="Brief summary of your issue" />
                  {errors.subject && <p className="mt-2 text-xs text-destructive">{errors.subject}</p>}
                </div>
                <div className="sm:col-span-2">
                  <label className={label} htmlFor="description">Description</label>
                  <textarea id="description" name="description" rows={6} className={field} placeholder="Please describe your issue in detail…" />
                  {errors.description && <p className="mt-2 text-xs text-destructive">{errors.description}</p>}
                </div>
                <div className="sm:col-span-2">
                  <label className={label} htmlFor="preferred_contact_method">Preferred Contact Method <span className="normal-case text-muted-foreground/70">(optional)</span></label>
                  <select id="preferred_contact_method" name="preferred_contact_method" defaultValue="" className={field}>
                    <option value="">No preference</option>
                    {CONTACT_METHODS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              {serverError && <p className="mt-6 text-sm text-destructive text-center">{serverError}</p>}

              <button
                type="submit"
                disabled={submitting}
                className="mt-8 w-full px-8 py-4 bg-primary text-primary-foreground font-semibold uppercase tracking-widest text-sm rounded-sm shadow-glow hover:translate-y-[-2px] transition disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? "Sending…" : "Submit Support Request"}
              </button>
              <p className="mt-6 text-center text-xs text-muted-foreground leading-relaxed max-w-xl mx-auto">
                By submitting this form you agree to our{" "}
                <Link to="/terms" className="text-primary hover:underline">Terms of Use</Link>{" "}
                and{" "}
                <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>.
              </p>
            </form>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}
